import { adminSupabase as supabase } from '@/lib/supabase'

export interface WaPartner {
  id: string
  slug: string
  display_name: string
  contact_email: string | null
  contact_phone: string | null
  max_org_slots: number
  billing_model: string
  daily_rate_agorot: number
  status: 'trial' | 'active' | 'suspended' | 'cancelled'
  trial_ends_at: string | null
  created_at: string
  updated_at: string
}

export interface PartnerOrgSlot {
  id?: string
  org_id: string
  partner_id: string | null
  partner_name?: string | null
  status: string | null
  phone_number: string | null
  last_connected_at: string | null
  daily_message_count: number | null
  notes: string | null
  registered_at: string | null
}

export interface WaDailyUsage {
  id: string
  partner_id: string
  org_id: string
  usage_date: string
  was_active: boolean
  messages_sent: number
  messages_received: number
  cost_agorot: number
}

function lastDayOfCalendarMonth(month: string): string {
  const [year, m] = month.split('-').map(Number)
  const end = new Date(year, m, 0)
  return end.toISOString().split('T')[0]
}

export async function listPartners(): Promise<WaPartner[]> {
  const { data, error } = await supabase.from('wa_partners').select('*').order('display_name')
  if (error) throw error
  return (data ?? []) as WaPartner[]
}

export async function getPartner(id: string): Promise<WaPartner> {
  const { data, error } = await supabase.from('wa_partners').select('*').eq('id', id).single()
  if (error) throw error
  return data as WaPartner
}

export async function getPartnerSlots(partnerId: string): Promise<PartnerOrgSlot[]> {
  const { data, error } = await supabase
    .from('partner_org_slots')
    .select('*')
    .eq('partner_id', partnerId)
    .order('org_id')
  if (error) throw error
  return (data ?? []) as PartnerOrgSlot[]
}

export async function getAllPartnerSlots(): Promise<PartnerOrgSlot[]> {
  const { data, error } = await supabase.from('partner_org_slots').select('*')
  if (error) throw error
  return (data ?? []) as PartnerOrgSlot[]
}

export async function getPartnerUsage(partnerId: string, month?: string): Promise<WaDailyUsage[]> {
  let query = supabase
    .from('wa_daily_usage')
    .select('*')
    .eq('partner_id', partnerId)
    .order('usage_date', { ascending: false })

  if (month) {
    const startDate = `${month}-01`
    const endDate = lastDayOfCalendarMonth(month)
    query = query.gte('usage_date', startDate).lte('usage_date', endDate)
  }

  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as WaDailyUsage[]
}

/** All `wa_daily_usage` rows for a calendar month (for billing aggregation). */
export async function listDailyUsageForMonth(month: string): Promise<WaDailyUsage[]> {
  const startDate = `${month}-01`
  const endDate = lastDayOfCalendarMonth(month)
  const { data, error } = await supabase
    .from('wa_daily_usage')
    .select('*')
    .gte('usage_date', startDate)
    .lte('usage_date', endDate)
    .order('usage_date', { ascending: false })
  if (error) throw error
  return (data ?? []) as WaDailyUsage[]
}

export type WaPartnerUpdate = Partial<
  Pick<WaPartner, 'daily_rate_agorot' | 'max_org_slots' | 'status' | 'contact_email' | 'contact_phone' | 'billing_model'>
>

export async function updatePartner(id: string, updates: WaPartnerUpdate): Promise<void> {
  const { error } = await supabase
    .from('wa_partners')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}
